/**
 * Custom hook for managing Hook Pipeline data
 * Uses TanStack Query for data fetching and caching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hooksApi } from '../api/hooks';
import { QUERY_KEYS } from '../queries/useAgentsQuery';
import type { HookConfig, HookConfigUpdate, HookReorderItem } from '../../types/hooks';

interface UseHooksPipelineOptions {
  agentType: string;
  userId?: string;
}

export function useHooksPipeline({ agentType, userId }: UseHooksPipelineOptions) {
  const queryClient = useQueryClient();
  const queryKey = ['hooks', 'pipeline', agentType, userId];

  // Fetch pipeline data
  const {
    data: pipeline,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await hooksApi.getPipeline(agentType, userId);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

  // Update hook mutation
  const updateHookMutation = useMutation({
    mutationFn: async ({
      hookName,
      data,
    }: {
      hookName: string;
      data: HookConfigUpdate;
    }) => {
      const response = await hooksApi.updateHook(agentType, hookName, data, userId);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch pipeline
      queryClient.invalidateQueries({ queryKey });
      // Also invalidate agent config since hook enabled state affects config.hooks.*
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(agentType) });
    },
  });

  // Reorder hooks mutation
  const reorderMutation = useMutation({
    mutationFn: async (hookOrders: HookReorderItem[]) => {
      const response = await hooksApi.reorder(agentType, hookOrders, userId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Also invalidate agent config since reordering affects config.hooks.*.sort_order
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(agentType) });
    },
  });

  // Reset hook mutation
  const resetHookMutation = useMutation({
    mutationFn: async (hookName: string) => {
      const response = await hooksApi.resetHook(agentType, hookName, userId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Also invalidate agent config since resetting affects config.hooks.*
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(agentType) });
    },
  });

  // Helper to toggle hook enabled status
  const toggleHookEnabled = async (hook: HookConfig) => {
    await updateHookMutation.mutateAsync({
      hookName: hook.hook_name,
      data: { enabled: !hook.enabled },
    });
  };

  // Helper to toggle hook visibility in Settings
  const toggleHookVisibility = async (hook: HookConfig) => {
    await updateHookMutation.mutateAsync({
      hookName: hook.hook_name,
      data: { show_in_settings: !hook.show_in_settings },
    });
  };

  // Helper to update hook params
  const updateHookParams = async (hookName: string, params: Record<string, any>) => {
    await updateHookMutation.mutateAsync({
      hookName,
      data: { params },
    });
  };

  // Helper to reorder hooks
  const reorderHooks = async (hookOrders: HookReorderItem[]) => {
    await reorderMutation.mutateAsync(hookOrders);
  };

  // Helper to reset hook to defaults
  const resetHook = async (hookName: string) => {
    await resetHookMutation.mutateAsync(hookName);
  };

  return {
    // Data
    pipeline,
    isLoading,
    error,

    // Mutations
    updateHook: updateHookMutation.mutateAsync,
    toggleHookEnabled,
    toggleHookVisibility,
    updateHookParams,
    reorderHooks,
    resetHook,

    // State
    isUpdating: updateHookMutation.isPending,
    isReordering: reorderMutation.isPending,
    isResetting: resetHookMutation.isPending,

    // Refetch
    refetch,
  };
}
